import React, { useContext } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, LoadingModule, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useFontFamily, { useFontFamilyForm } from '@hooks/useFontFamily'
import useFont from '@hooks/useFont'
import useFonts from '@hooks/useFonts'

import FontListItem from './FontListItem'

const createOrUpdateFontFamily = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createFontFamily, updateFontFamily },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const fontFamilyPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Font ${text}`,
  }

  if (entityState.id) return updateFontFamily(fontFamilyPayload)

  return createFontFamily(fontFamilyPayload)
}

const CreateOrEditFontFamilyModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditFontFamilyModal', showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { fontFamily } = modalPayload

  const fontFamilyPayload = useFontFamily(fontFamily)

  const {
    callbacks: {
      createFontFamily: createFn,
      updateFontFamily: updateFn,
    },
    creating,
    updating,
  } = fontFamilyPayload

  const fontsPayload = useFonts({ filters: { font_family_id: fontFamily?.id }, performHttpRequests: !!fontFamily?.id })
  const { filteredFonts, hasFilteredFonts, loading } = fontsPayload

  const formPayload = useFontFamilyForm(fontFamily, { validateOn: 'change' })

  const {
    callbacks: { createOrEditFont },
  } = useFont({ font_family_id: fontFamily?.id })

  const {
    entityState,
    entityState: {
      id,
      title,
    },
    handlers,
    saveEnabled,
  } = formPayload

  const isEditing = !!id
  const saveOrCreateButtonText = isEditing ? 'Save Changes' : 'Add Font Family'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Font Family" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <LoadingModule loading={loading} />

          {!loading && (
            <>
              <Form.Field direction="column" label="* Title">
                <input name="title" type="text" value={title} {...handlers} />
              </Form.Field>

              {hasFilteredFonts && (
                <Box flexDirection="column">
                  <SectionDivider boxProps={{ marginTop: 'large' }}>FONT WEIGHTS</SectionDivider>

                  {filteredFonts.map(font => (
                    <FontListItem callbacks={callbacks} font={font} key={font.id} />
                  ))}
                </Box>
              )}

              {isEditing && (
                <Form.Field label="More Options" marginTop={hasFilteredFonts ? 0 : 'large'}>
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => createOrEditFont()}
                    size="medium"
                  >
                    Add Font
                  </Button>
                </Form.Field>
              )}
            </>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateFontFamily(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditFontFamilyModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditFontFamilyModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
